import { useState, useEffect } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { EzLogo, AboutUsLink } from "@/pages/LandingPage";
import {
  ArrowLeft, ArrowRight, CheckCircle2, Upload, X,
  Clock, Users, Phone, MapPin, Laptop, Heart, FileText, UserCheck, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import mixpanel from "@/mixpanel";
import { api } from "@/lib/api";
import { reportError } from "@/lib/errors";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const slideVariants = {
  enter: { opacity: 0, x: 30 },
  center: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -30 },
};

const formatPhone = (value) => {
  const digits = value.replace(/\D/g, "").slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const US_STATES = [
  "Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut",
  "Delaware","District of Columbia","Florida","Georgia","Hawaii","Idaho","Illinois",
  "Indiana","Iowa","Kansas","Kentucky","Louisiana","Maine","Maryland","Massachusetts",
  "Michigan","Minnesota","Mississippi","Missouri","Montana","Nebraska","Nevada",
  "New Hampshire","New Jersey","New Mexico","New York","North Carolina","North Dakota",
  "Ohio","Oklahoma","Oregon","Pennsylvania","Puerto Rico","Rhode Island",
  "South Carolina","South Dakota","Tennessee","Texas","Utah","Vermont","Virginia",
  "Washington","West Virginia","Wisconsin","Wyoming",
];

const STEPS = [
  {
    id: "full_time",
    title: "Are you available to work full-time?",
    subtitle: "This is a 40-hour/week role with daytime hours. There is flexibility and benefits, but it must be your primary job.",
    icon: <Clock className="w-5 h-5" />,
    pitch: "We offer a stable full-time position with health insurance, PTO, and career advancement. Daytime hours mean your evenings are yours.",
    type: "boolean",
    field: "can_work_full_time",
  },
  {
    id: "outbound_experience",
    title: "Do you have experience making outbound phone calls?",
    subtitle: "Sales calls, cold calls, appointment setting, lead generation — any outbound calling counts.",
    icon: <Phone className="w-5 h-5" />,
    pitch: "Our top performers come from diverse sales backgrounds — insurance, telecom, SaaS, home services. What matters is your comfort on the phone.",
    type: "boolean",
    field: "outbound_call_experience",
  },
  {
    id: "calls_per_day",
    title: "On average, how many outbound phone calls did you make per day in past roles?",
    subtitle: "Pick the range that best represents your typical daily volume.",
    icon: <Phone className="w-5 h-5" />,
    type: "select",
    field: "calls_per_day",
    options: [
      { value: "under_25", label: "Under 25 calls" },
      { value: "25-50", label: "25 to 50 calls" },
      { value: "50-75", label: "50 to 75 calls" },
      { value: "75-100", label: "75 to 100 calls" },
      { value: "100+", label: "100+ calls" },
    ],
  },
  {
    id: "state",
    title: "What state do you live in?",
    subtitle: "This helps us plan your schedule around coverage needs.",
    icon: <MapPin className="w-5 h-5" />,
    pitch: "We have reps across the country. Knowing your location helps us set you up for success from day one.",
    type: "state_dropdown",
    field: "timezone_or_state",
  },
  {
    id: "remote",
    title: "Have you worked remotely before?",
    subtitle: "Our team is fully remote — we want to know if you're comfortable in that environment.",
    icon: <Laptop className="w-5 h-5" />,
    pitch: "Our team is 100% remote. We provide the tools and training — you bring the self-discipline and drive.",
    type: "boolean",
    field: "worked_remotely",
  },
  {
    id: "why_seniors",
    title: "Why do you enjoy talking to seniors?",
    subtitle: "Tell us about a moment, experience, or relationship with an older adult.",
    icon: <Heart className="w-5 h-5" />,
    pitch: "Our sales reps call seniors every day. A genuine connection to this population makes the difference between a good rep and a great one.",
    type: "textarea",
    field: "why_like_seniors",
    placeholder: "Tell us about a moment, a person, or an experience that makes you passionate about helping older adults...",
  },
  {
    id: "resume",
    title: "Upload your resume",
    subtitle: "This is the most important part of your application. PDF, DOC, or DOCX accepted.",
    icon: <FileText className="w-5 h-5" />,
    pitch: "We value all types of sales experience — inside sales, field sales, retail, B2B, B2C. Whatever your background, we want to see it.",
    type: "resume",
  },
  {
    id: "linkedin",
    title: "What's your LinkedIn profile URL?",
    subtitle: "This helps us learn more about your background.",
    icon: <UserCheck className="w-5 h-5" />,
    pitch: "A LinkedIn profile helps us get a fuller picture of your experience and professional network. Don't worry if yours isn't up to date.",
    type: "linkedin",
    field: "linkedin_url",
  },
  {
    id: "references",
    title: "Can you provide two references from former managers?",
    subtitle: "During the interview process, we'll ask for contact info for two managers who can speak to your work. No need to provide them now.",
    icon: <UserCheck className="w-5 h-5" />,
    pitch: "We keep the reference process simple — just a heads-up that we'll ask later. Most candidates find this is a quick conversation with past managers.",
    type: "boolean",
    field: "can_provide_references",
  },
  {
    id: "contact",
    title: "Your contact information",
    subtitle: "Almost done! We'll use this to follow up on your application.",
    icon: <Users className="w-5 h-5" />,
    pitch: "Qualified candidates hear back within 48 hours. We move fast because we know great sales talent doesn't wait.",
    type: "contact",
  },
];

export default function SalesApplicationSurvey() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get("user");

  // Skip first step if already answered on landing page
  const preAnswered = location.state?.can_work_full_time !== undefined && location.state?.can_work_full_time !== null;
  const [step, setStep] = useState(preAnswered ? 1 : 0);
  const [form, setForm] = useState({
    can_work_full_time: location.state?.can_work_full_time ?? null,
    outbound_call_experience: null,
    calls_per_day: "",
    timezone_or_state: "",
    worked_remotely: null,
    can_provide_references: null,
    why_like_seniors: "",
    resume: null,
    linkedin_url: "",
    full_name: "",
    email: "",
    phone: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [stepError, setStepError] = useState("");
  // Resume already uploaded via the Indeed flow (no re-upload needed)
  const [indeedResume, setIndeedResume] = useState({ has: false, filename: "" });

  // Pre-fill from Indeed
  useEffect(() => {
    if (!userId) return;
    api.get(`${API}/indeed-prefill/${userId}`)
      .then(r => r.data)
      .then(data => {
        if (data.found) {
          setForm(prev => ({
            ...prev,
            full_name: data.name || prev.full_name,
            email: data.email || prev.email,
            phone: data.phone ? formatPhone(data.phone.replace("+1", "")) : prev.phone,
          }));
          setIndeedResume({ has: !!data.has_resume, filename: (data.has_resume && data.resume_filename) || "" });
        }
      })
      .catch(() => {});
  }, [userId]);

  const totalSteps = STEPS.length;
  const current = STEPS[step];
  const progress = ((step + 1) / totalSteps) * 100;

  const updateForm = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setStepError("");
  };

  const validate = () => {
    const s = STEPS[step];
    if (s.type === "boolean" && form[s.field] === null) {
      return "Please select an option.";
    }
    if (s.type === "select" && !form[s.field]) {
      return "Please select an option.";
    }
    if (s.type === "text" && !form[s.field]?.trim()) {
      return "Please fill in this field.";
    }
    if (s.type === "state_dropdown" && !form[s.field]) {
      return "Please select a state.";
    }
    if (s.type === "textarea" && !form[s.field]?.trim()) {
      return "Please write a response.";
    }
    if (s.type === "resume" && !form.resume && !indeedResume.has) {
      return "Please upload your resume.";
    }
    if (s.type === "linkedin" && !form.linkedin_url?.trim()) {
      return "Please enter your LinkedIn profile URL.";
    }
    if (s.type === "contact") {
      if (!form.full_name.trim()) return "Please enter your name.";
      if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) return "Please enter a valid email.";
      if (!form.phone.trim() || form.phone.replace(/\D/g, "").length < 10) return "Please enter a valid 10-digit phone.";
    }
    return "";
  };

  const next = () => {
    const err = validate();
    if (err) { setStepError(err); return; }
    setStepError("");
    setStep(s => Math.min(s + 1, totalSteps - 1));
  };

  const back = () => {
    if (step === 0) {
      navigate(`/sales?${searchParams.toString()}`);
    } else {
      setStepError("");
      setStep(s => s - 1);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) { toast.error("Resume must be under 20MB"); return; }
    updateForm("resume", file);
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) { setStepError(err); return; }
    setSubmitting(true);

    try {
      const fd = new FormData();
      fd.append("job_type", "sales");
      fd.append("full_name", form.full_name.trim());
      fd.append("email", form.email.trim());
      fd.append("phone", form.phone.trim());
      fd.append("can_work_full_time", form.can_work_full_time ? "true" : "false");
      fd.append("outbound_call_experience", form.outbound_call_experience ? "true" : "false");
      fd.append("calls_per_day", form.calls_per_day);
      fd.append("timezone_or_state", form.timezone_or_state.trim());
      fd.append("worked_remotely", form.worked_remotely ? "true" : "false");
      fd.append("can_work_independently", form.worked_remotely ? "true" : "false");
      fd.append("why_like_seniors", form.why_like_seniors.trim());
      fd.append("references_json", JSON.stringify(form.can_provide_references ? [{"willing": true}] : [{"willing": false}]));
      if (form.resume) fd.append("resume", form.resume);
      if (form.linkedin_url.trim()) fd.append("linkedin_url", form.linkedin_url.trim());
      if (userId) fd.append("indeed_ref_id", userId);

      let resp;
      try {
        resp = await api.post(`${API}/applications`, fd, {
          timeout: 60_000,
          retry: false,
        });
      } catch (err) {
        const message = err.response?.status === 409
          ? "You've already applied for this position with this email."
          : err.response?.data?.detail || "Submission failed";
        reportError(err, message);
        return;
      }

      const data = resp.data;
      mixpanel.identify(form.email);
      mixpanel.people.set({ "$email": form.email, "$name": form.full_name, "$phone": form.phone });
      mixpanel.track("salesApplicationSubmitted", { job_type: "sales", score_total: data.score_total });
      navigate(`/sales/willo`, { state: { appId: data.id, scoring: true } });
    } finally {
      setSubmitting(false);
    }
  };

  const renderStepContent = () => {
    const s = STEPS[step];

    if (s.type === "boolean") {
      const val = form[s.field];
      return (
        <div className="mt-6 flex flex-col gap-3" data-testid={`step-${s.id}`}>
          {[
            { value: true, label: "Yes" },
            { value: false, label: "No" },
          ].map(opt => (
            <button
              key={String(opt.value)}
              data-testid={`option-${s.field}-${opt.value}`}
              onClick={() => updateForm(s.field, opt.value)}
              className={`w-full p-4 rounded-xl border-2 text-left font-medium transition-all ${
                val === opt.value
                  ? "border-ez-teal bg-ez-teal/5 text-ez-teal"
                  : "border-slate-200 hover:border-slate-300 text-slate-700"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      );
    }

    if (s.type === "select") {
      return (
        <div className="mt-6 flex flex-col gap-3" data-testid={`step-${s.id}`}>
          {s.options.map(opt => (
            <button
              key={opt.value}
              data-testid={`option-${opt.value}`}
              onClick={() => updateForm(s.field, opt.value)}
              className={`w-full p-4 rounded-xl border-2 text-left font-medium transition-all ${
                form[s.field] === opt.value
                  ? "border-ez-teal bg-ez-teal/5 text-ez-teal"
                  : "border-slate-200 hover:border-slate-300 text-slate-700"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      );
    }

    if (s.type === "text") {
      return (
        <div className="mt-6" data-testid={`step-${s.id}`}>
          <Input
            data-testid={`input-${s.field}`}
            value={form[s.field]}
            onChange={e => updateForm(s.field, e.target.value)}
            placeholder={s.placeholder}
            className="h-14 rounded-xl border-slate-200 bg-slate-50 focus:bg-white focus:border-ez-teal focus:ring-ez-teal text-lg"
          />
        </div>
      );
    }

    if (s.type === "state_dropdown") {
      return (
        <div className="mt-6" data-testid={`step-${s.id}`}>
          <select
            data-testid="select-state"
            value={form[s.field]}
            onChange={e => updateForm(s.field, e.target.value)}
            className="w-full h-14 rounded-xl border-slate-200 bg-slate-50 focus:bg-white focus:border-ez-teal focus:ring-ez-teal text-base px-4 appearance-none cursor-pointer"
          >
            <option value="">Select a state...</option>
            {US_STATES.map(st => (
              <option key={st} value={st}>{st}</option>
            ))}
          </select>
        </div>
      );
    }

    if (s.type === "textarea") {
      return (
        <div className="mt-6" data-testid={`step-${s.id}`}>
          <Textarea
            data-testid={`input-${s.field}`}
            value={form[s.field]}
            onChange={e => updateForm(s.field, e.target.value)}
            placeholder={s.placeholder}
            className="min-h-[140px] rounded-xl border-slate-200 bg-slate-50 focus:bg-white focus:border-ez-teal focus:ring-ez-teal text-base resize-none"
            rows={5}
          />
        </div>
      );
    }

    if (s.type === "resume") {
      const hasIndeedResume = indeedResume.has && !form.resume;
      return (
        <div className="mt-6" data-testid={`step-${s.id}`}>
          <div
            data-testid={hasIndeedResume ? "sales-resume-from-indeed" : "sales-resume-drop-zone"}
            className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all ${
              form.resume || hasIndeedResume ? "border-emerald-300 bg-emerald-50" : "border-slate-200 hover:border-ez-teal/40 bg-slate-50"
            }`}
            onDrop={handleDrop}
            onDragOver={e => e.preventDefault()}
            onClick={() => document.getElementById("sales-resume-input")?.click()}
          >
            {form.resume ? (
              <div className="flex flex-col items-center gap-3">
                <CheckCircle2 className="w-10 h-10 text-emerald-500" />
                <p className="font-semibold text-ez-teal">{form.resume.name}</p>
                <p className="text-sm text-slate-500">{(form.resume.size / 1024).toFixed(0)} KB</p>
                <button
                  data-testid="remove-sales-resume-btn"
                  onClick={e => { e.stopPropagation(); updateForm("resume", null); }}
                  className="text-sm text-red-500 hover:text-red-600 flex items-center gap-1 mt-1"
                >
                  <X className="w-3 h-3" /> Remove
                </button>
              </div>
            ) : hasIndeedResume ? (
              <div className="flex flex-col items-center gap-3">
                <CheckCircle2 className="w-10 h-10 text-emerald-500" />
                <p className="font-semibold text-ez-teal">Your Indeed resume is already attached</p>
                {indeedResume.filename && (
                  <p className="text-sm text-slate-500">{indeedResume.filename}</p>
                )}
                <p
                  data-testid="replace-sales-indeed-resume-btn"
                  className="text-sm text-ez-teal hover:underline mt-1"
                >
                  Click or drop a file to replace it
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3">
                <Upload className="w-10 h-10 text-ez-teal" />
                <p className="font-semibold text-ez-teal">Drop your resume here</p>
                <p className="text-sm text-slate-500">or click to browse — PDF, DOC, DOCX</p>
              </div>
            )}
          </div>
          <input
            id="sales-resume-input"
            data-testid="sales-resume-file-input"
            type="file"
            accept=".pdf,.doc,.docx"
            className="hidden"
            onChange={e => {
              const f = e.target.files?.[0];
              if (f && f.size > 20 * 1024 * 1024) { toast.error("Resume must be under 20MB"); e.target.value = ""; }
              else if (f) updateForm("resume", f);
            }}
          />
        </div>
      );
    }

    if (s.type === "linkedin") {
      return (
        <div className="mt-6" data-testid={`step-${s.id}`}>
          <Input
            data-testid="input-linkedin-url"
            value={form.linkedin_url}
            onChange={e => updateForm("linkedin_url", e.target.value)}
            placeholder="https://linkedin.com/in/your-profile"
            className="h-14 rounded-xl border-slate-200 bg-slate-50 focus:bg-white focus:border-ez-teal focus:ring-ez-teal text-base"
          />
          <p className="text-xs text-slate-400 mt-2 font-body">Paste your full LinkedIn profile URL.</p>
        </div>
      );
    }

    if (s.type === "contact") {
      return (
        <div className="space-y-5 mt-6" data-testid={`step-${s.id}`}>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Full Name</label>
            <Input
              data-testid="input-sales-full-name"
              value={form.full_name}
              onChange={e => updateForm("full_name", e.target.value)}
              placeholder="Jane Smith"
              className="h-12 rounded-xl border-slate-200 bg-slate-50 focus:bg-white focus:border-ez-teal focus:ring-ez-teal text-base"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Email</label>
            <Input
              data-testid="input-sales-email"
              type="email"
              value={form.email}
              onChange={e => updateForm("email", e.target.value)}
              placeholder="jane@example.com"
              className="h-12 rounded-xl border-slate-200 bg-slate-50 focus:bg-white focus:border-ez-teal focus:ring-ez-teal text-base"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Phone</label>
            <Input
              data-testid="input-sales-phone"
              type="tel"
              value={form.phone}
              onChange={e => updateForm("phone", formatPhone(e.target.value))}
              placeholder="(555) 123-4567"
              className="h-12 rounded-xl border-slate-200 bg-slate-50 focus:bg-white focus:border-ez-teal focus:ring-ez-teal text-base"
            />
          </div>
        </div>
      );
    }

    return null;
  };

  const isLastStep = step === totalSteps - 1;

  return (
    <div className="min-h-screen bg-white" data-testid="sales-application-survey-page">
      {/* Top Bar */}
      <div className="sticky top-0 z-50 bg-white border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4 sm:gap-6">
            <EzLogo size="h-7" />
            <AboutUsLink />
          </div>
          <span className="text-sm text-slate-500 font-body" data-testid="sales-step-counter">
            Step {step + 1} of {totalSteps}
          </span>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 pb-4">
          <Progress value={progress} className="h-2.5 rounded-full bg-slate-100" data-testid="sales-survey-progress" />
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 lg:py-16">
        <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
          {/* Main Content */}
          <div className="lg:col-span-3">
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.25 }}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-ez-blue/10 flex items-center justify-center text-ez-blue">
                    {current.icon}
                  </div>
                  <span className="text-xs font-semibold text-ez-blue uppercase tracking-wider font-body">
                    {current.id === "contact" ? "Final Step" : `Question ${step + 1}`}
                  </span>
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-ez-teal font-heading tracking-tight mb-2" data-testid="sales-step-title">
                  {current.title}
                </h2>
                <p className="text-slate-500 mb-2" data-testid="sales-step-subtitle">{current.subtitle}</p>

                {renderStepContent()}
              </motion.div>
            </AnimatePresence>

            {/* Navigation */}
            {stepError && (
              <div data-testid="sales-step-error" className="mt-8 flex items-center gap-2 px-4 py-2.5 rounded-xl bg-red-50 border border-red-100">
                <div className="w-1.5 h-1.5 rounded-full bg-red-400 shrink-0" />
                <p className="text-sm text-red-600">{stepError}</p>
              </div>
            )}
            <div className={`flex items-center justify-between ${stepError ? "mt-4" : "mt-10"} pt-8 border-t border-slate-100`}>
              <Button
                data-testid="sales-back-btn"
                variant="ghost"
                onClick={back}
                className="text-slate-600 hover:text-ez-teal rounded-xl"
              >
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>

              {isLastStep ? (
                <Button
                  data-testid="sales-submit-btn"
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="rounded-full bg-ez-blue text-white hover:bg-[#5A9BCF] px-8 py-3 font-semibold shadow-lg shadow-blue-100 hover:-translate-y-0.5 active:translate-y-0 transition-transform"
                >
                  {submitting ? "Submitting..." : "Submit Application"}
                </Button>
              ) : (
                <Button
                  data-testid="sales-next-btn"
                  onClick={next}
                  className="rounded-full bg-ez-blue text-white hover:bg-[#5A9BCF] px-8 py-3 font-semibold shadow-lg shadow-blue-100 hover:-translate-y-0.5 active:translate-y-0 transition-transform"
                >
                  Continue <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>
          </div>

          {/* Pitch Sidebar */}
          <div className="lg:col-span-2 hidden lg:block">
            <AnimatePresence mode="wait">
              {current.pitch && (
                <motion.div
                  key={step}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.3, delay: 0.1 }}
                  className="sticky top-32"
                >
                  <div className="bg-ez-surface rounded-2xl border border-slate-100 p-8" data-testid="sales-pitch-panel">
                    <div className="w-10 h-10 rounded-xl bg-ez-blue/10 flex items-center justify-center text-ez-blue mb-4">
                      <Sparkles className="w-5 h-5" />
                    </div>
                    <p className="text-slate-700 leading-relaxed font-body">
                      {current.pitch}
                    </p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}
